import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../auth/AuthContext';

export type NotificationType =
  // Type unifié (Lot 4) — la "source" précise est dans metadata.source
  | 'action_a_suivre'
  // Notifications directes (push immédiat lors d'une action utilisateur)
  | 'assignation'
  | 'tache_assignee'
  | 'mention'
  | 'point_critique'
  // Types legacy — conservés en lecture pour les notifications historiques
  | 'tache_echeance'
  | 'devis_sans_reponse'
  | 'proposition_sans_reponse'
  | 'prestation_a_relancer'
  | 'contrat_non_signe'
  | 'facture_impayee'
  | 'acompte_attente';

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  titre: string;
  message: string | null;
  lien: string | null;
  prestation_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  lu: boolean;
  lu_at: string | null;
  created_at: string;
  metadata: any;
}

interface Ctx {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const NotificationsCtx = createContext<Ctx | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setNotifications([]); setLoading(false); return; }
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(100);
    setNotifications((data || []) as Notification[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('notifications-' + user.id)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => { refresh(); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, refresh]);

  const markAsRead = async (id: string) => {
    await supabase.from('notifications').update({ lu: true, lu_at: new Date().toISOString() }).eq('id', id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, lu: true } : n)));
  };

  const markAllAsRead = async () => {
    if (!user) return;
    await supabase.from('notifications').update({ lu: true, lu_at: new Date().toISOString() }).eq('user_id', user.id).eq('lu', false);
    setNotifications((prev) => prev.map((n) => ({ ...n, lu: true })));
  };

  const deleteNotification = async (id: string) => {
    await supabase.from('notifications').delete().eq('id', id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const unreadCount = notifications.filter((n) => !n.lu).length;

  return (
    <NotificationsCtx.Provider value={{ notifications, unreadCount, loading, markAsRead, markAllAsRead, deleteNotification, refresh }}>
      {children}
    </NotificationsCtx.Provider>
  );
}

export function useNotifications() {
  const c = useContext(NotificationsCtx);
  if (!c) throw new Error('useNotifications must be used within NotificationsProvider');
  return c;
}
